import React, { createContext, useContext, useState } from "react";
import { apiConnect } from "../api/axios";
import toast from "react-hot-toast";

const ResumeContext = createContext();

export const ResumeProvider = ({ children }) => {
  const [allResumes, setAllResumes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resumeDetails, setResumeDetails] = useState(false);

  function handleError(error) {
    const { response } = error;
    toast.error(
      response?.data?.message ?? "An error occurred, please try again!",
      { position: "top-center" }
    );
  }

  const getAllResumes = async () => {
    try {
      setIsLoading(true);
      const response = await apiConnect.getAllResume();

      if (response.status === 200) {
        setAllResumes(response.data);
      } else {
        toast.error("Failed to fetch resumes. Please try again later.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createResume = async (newResume) => {
    try {
      setIsLoading(true);

      // Replace with your actual API call for creating resume
      const response = await apiConnect.createResume(newResume);

      if (response.status === 201) {
        // Successful creation, you may update the state or perform other actions
        toast.success("Resume created successfully!");
      } else {
        toast.error("Failed to create resume. Please try again later.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getResumeById = async (id) => {
    try {
      setIsLoading(true);
      const response = await apiConnect.getResumeById(id);

      if (response.status === 200) {
        // Handle successful response
        setResumeDetails(response.data);
      } else if (response.status === 404) {
        toast.error("Resume not found.");
      } else {
        toast.error("Failed to fetch resume details. Please try again later.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateResumeById = async (id, updatedResume) => {
    try {
      setIsLoading(true);
      const response = await apiConnect.updateResumeById(id, updatedResume);

      if (response.status === 200) {
        // Handle successful update
        toast.success("Resume Updated!");
      } else if (response.status === 404) {
        toast.error("Resume not found.");
      } else {
        toast.error("Failed to update resume. Please try again later.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteResumeById = async (id) => {
    try {
      setIsLoading(true);
      const response = await apiConnect.deleteResumeById(id);

      if (response.status === 204) {
        // Handle successful deletion
      } else if (response.status === 404) {
        toast.error("Resume not found.");
      } else {
        toast.error("Failed to delete resume. Please try again later.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResumeContext.Provider
      value={{
        allResumes,
        isLoading,
        resumeDetails,
        getAllResumes,
        getResumeById,
        updateResumeById,
        deleteResumeById,
        createResume,
      }}
    >
      {children}
    </ResumeContext.Provider>
  );
};

export const useResumeContext = () => {
  const context = useContext(ResumeContext);
  if (!context) {
    throw new Error("useResumeContext must be used within a ResumeProvider");
  }
  return context;
};
